import { useState } from "react";
import { useMutation } from "react-query";
import { MdOutlineLink } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { EVENTS } from "../../config/apiConfig";
import Modal from "../modals/Modal";
import ClientPicker from "../crm/ClientPicker";

/*
  Vincular uma cliente a este evento à mão.

  Serve para quem não passou pelo onboarding do evento (nem usou o cupom) mas
  veio por ele. Uma cliente pertence a um evento só: se ela já está em outro, o
  servidor avisa e a tela pergunta antes de passá-la para este. O vínculo
  entra no histórico de relacionamento dela com o nome de quem fez.
*/

const LinkClientModal = ({ event, onClose, onLinked }) => {
  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  // { currentEvent: {id, name} } quando ela já está em outro evento.
  const [conflict, setConflict] = useState(null);

  const link = useMutation(
    ({ move }) =>
      axios.post(`${EVENTS.ONE}/${event.id}/clients`, {
        companyId: client.id,
        move,
      }),
    {
      onSuccess: ({ data }) => {
        const result = data.data;

        if (!result.linked) {
          setError("Esta cliente já está neste evento.");
          return;
        }
        onLinked?.(client, result);
      },
      onError: (err) => {
        const body = err.response?.data;

        if (err.response?.status === 409 && body?.code === "EVENT_LINK_CONFLICT") {
          setConflict(body);
          setError(null);
          return;
        }
        setError(body?.message || "Não foi possível vincular a cliente.");
      },
    }
  );

  const pick = (value) => {
    setClient(value);
    setConflict(null);
    setError(null);
  };

  return (
    <Modal
      isOpen
      onClose={() => (link.isLoading ? null : onClose())}
      title="Vincular cliente"
      subtitle={event.name}
      icon={MdOutlineLink}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={link.isLoading}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancelar
          </button>
          {conflict ? (
            <button
              type="button"
              onClick={() => link.mutate({ move: true })}
              disabled={link.isLoading}
              className="rounded-xl bg-amber-600 px-5 py-2 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {link.isLoading ? "Passando..." : "Passar para este evento"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setError(null);
                link.mutate({ move: false });
              }}
              disabled={!client || link.isLoading}
              className="rounded-xl bg-pink-600 px-5 py-2 text-sm font-bold text-white hover:bg-pink-700 disabled:opacity-50"
            >
              {link.isLoading ? "Vinculando..." : "Vincular ao evento"}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Cliente
          </p>
          <ClientPicker value={client} onChange={pick} disabled={link.isLoading} />
          <p className="mt-1 text-[11px] text-slate-500">
            Busque pelo nome, e-mail, telefone ou #id da conta.
          </p>
        </div>

        {conflict ? (
          <div
            role="alert"
            className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-200"
          >
            <p className="font-semibold">
              {client?.name} já está no evento &quot;
              {conflict.currentEvent?.name || `#${conflict.currentEvent?.id}`}&quot;.
            </p>
            <p className="mt-0.5 text-xs">
              Uma cliente conta em um evento só. Passando para este, ela sai dos
              números do outro e a troca fica no histórico dela.
            </p>
          </div>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default LinkClientModal;
